import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaClipboardCheck,
  FaBalanceScale
} from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from '../components/HtmlContent';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';

const PublicationEthicsPage = () => {
  const location = useLocation();
  const { content, loading } = usePageContent('publication_ethics');

  // Smooth scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const titles = content?.titles || {};

  // Wait for the saved text rather than flashing the built-in default first
  if (loading) return <div className="min-h-screen" />;

  if (isRawHtmlPage(content)) {
    return <RawHtmlPage html={content.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Publication Ethics</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-10">
          <div>
            <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
              COPE ETHICS STANDARDS & GOVERNANCE
            </span>
            <h1 className="page-title">
              {titles.page_title || content?.page_title || 'Publication Ethics'}
            </h1>
            {content?.overview && (
              <div className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed border-l-4 border-[#03478E] pl-4 bg-blue-50/40 py-2 rounded-r-xl">
                <HtmlContent content={content.overview} />
              </div>
            )}
          </div>

          {/* ================= FULL PAGE TEXT (IF ENTERED AS SINGLE DOCUMENT) ================= */}
          {content?.full_content ? (
            <div className="prose max-w-none text-gray-800 leading-relaxed space-y-6 border-t border-gray-100 pt-6">
              <HtmlContent content={content.full_content} />
            </div>
          ) : (
            /* ================= COHESIVE SINGLE PAGE SECTIONS (ALL OPEN) ================= */
            <div className="space-y-10">

              {/* 8.1 Conflict of Interest */}
              <section
                id="conflict-of-interest"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-2 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.conflict_of_interest || 'Conflict of Interest'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.conflict_of_interest}
                  fallback="All authors, peer reviewers, and editors must disclose any financial, personal, or professional relationships that could inappropriately influence or bias their work."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                />
              </section>

              {/* 8.2 Research Ethics & Patient Welfare */}
              <section
                id="research-ethics"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.research_ethics || 'Research Ethics & Patient Welfare'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.research_ethics}
                  fallback="All research involving human subjects, patient biological samples, or clinical data must comply with the Declaration of Helsinki and have formal Institutional Ethics Committee (IEC/IRB) approval."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                />
              </section>

              {/* 8.3 Originality & Similarity */}
              <section
                id="originality"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.originality || 'Originality & Duplicate Submission'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.originality}
                  fallback="Manuscripts submitted to AJHM must be original and not currently submitted to or published in any other journal in whole or in part, in English or any other language."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                />
              </section>

              {/* 8.4 Plagiarism Screening */}
              <section
                id="plagiarism"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.plagiarism || 'Plagiarism & Similarity Screening'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.plagiarism}
                  fallback="All submissions are screened with anti-plagiarism detection software upon initial editorial triage. The journal enforces a strict maximum acceptable similarity threshold of less than 10% (excluding references and standard methodological descriptions)."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                />
              </section>

              {/* 8.5 Corrections */}
              <section
                id="corrections"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.corrections || 'Post-Publication Corrections (Errata & Corrigenda)'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.corrections}
                  fallback="Errors that do not affect the main conclusions of a published article may be corrected via a formal Erratum (publisher error) or Corrigendum (author error), permanently linked to the original DOI."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                />
              </section>

              {/* 8.6 Retractions */}
              <section
                id="retractions"
                className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
              >
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="section-heading">
                    {titles.retractions || 'Retraction Criteria & Policy'}
                  </h2>
                </div>

                <HtmlContent
                  content={content?.retractions}
                  fallback="Articles found to contain fraudulent data, unresolvable ethical violations, severe honest errors invalidating findings, or major copyright infringement will be formally retracted in strict compliance with COPE Retraction Guidelines."
                  className="text-sm sm:text-base text-gray-700 leading-relaxed font-sans bg-red-50/50 p-5 rounded-2xl border border-red-100 text-red-900"
                />
              </section>

            </div>
          )}

          {/* Actions (Single Page Flow) */}
          <div className="border-t border-gray-100 pt-8 flex flex-wrap items-center justify-end gap-3">
            <Link
              to="/peer-review"
              className="inline-flex items-center gap-2 py-2.5 px-4 bg-white border border-gray-200 text-[#03478E] hover:bg-gray-50 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
            >
              <FaBalanceScale className="text-xs" />
              <span>Peer Review Process</span>
            </Link>
            <Link
              to="/information-for-authors"
              className="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
            >
              <FaClipboardCheck className="text-xs" />
              <span>Author Guidelines</span>
            </Link>
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 py-2.5 px-5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-bold rounded-full uppercase tracking-wider transition-all shadow-xs"
            >
              <span>Submit Manuscript</span>
              <FaArrowRight className="text-[10px]" />
            </Link>
          </div>

        </main>
      </div>
    </div>
  );
};

export default PublicationEthicsPage;
